/**
 * Login Helper Functions
 *
 * Small utility functions specific to login flow
 */

export const LOCAL_AUTH_HOSTS = new Set([
  "localhost",
  "127.0.0.1",
  "10.0.2.2",
  "openam",
]);

export const isLocalAuthHost = (hostname = globalThis.location.hostname) => {
  return LOCAL_AUTH_HOSTS.has(hostname);
};

/**
 * Sanitize credential value
 * Removes dashes from phone format (XXX-XXX-XXXX → XXXXXXXXXX)
 *
 * @param {string} value - The credential value to sanitize
 * @returns {string} - The sanitized value
 */
export const sanitizeValue = (value) => {
  // If the value matches the number format (XXX-XXX-XXXX), remove the dashes
  if (/^\d{3}-\d{3}-\d{4}$/.test(value)) {
    return value.replace(/-/g, "");
  }
  return value;
};

/**
 * Get background image path based on device and app mode
 *
 * @param {string} device - Device type (mobile/tablet/desktop)
 * @param {boolean} isApp - Whether this is the mobile app
 * @returns {string} - Path to the background image
 */
export const getBackgroundImagePath = (device, isApp) => {
  // App mobile has specific background
  if (isApp && device === "mobile") {
    return "/background-image-app.webp";
  }

  // Regular device-based backgrounds
  switch (device) {
    case "tablet":
      return "/background-image-tablet.webp";
    case "mobile":
      return "/background-image-mobile.webp";
    default:
      return "/background-image-desktop.webp";
  }
};

/**
 * Whether an authorize request came from the native app rather than a browser.
 *
 * Under Entra both surfaces share one app registration, so the client id no
 * longer tells them apart. The redirect URI still does: the native shell
 * registers a custom scheme (exp+engage://redirect) while the web app always
 * comes back over http(s). Also true for ForgeRock, where native authorizes as
 * "EngageMobile".
 *
 * @param {string} redirectUri - redirect_uri from the authorize request
 * @returns {boolean}
 */
export const isNativeAppRedirect = (redirectUri) => {
  if (!redirectUri) {
    return false;
  }

  const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(redirectUri)?.[1];
  return !!scheme && !["http", "https"].includes(scheme.toLowerCase());
};

/**
 * Validate required OAuth URL parameters
 *
 * @param {URLSearchParams} searchParams - URL search parameters
 * @returns {Object|null} - Params object if valid, null if invalid
 */
export const validateUrlParams = (searchParams) => {
  const required = [
    "redirect_uri",
    "client_id",
    "state",
    "code_challenge",
    "code_challenge_method",
    "response_type",
    "scope",
  ];

  // Validate required params
  for (const key of required) {
    if (!searchParams.get(key)) return null;
  }

  // Preserve ALL params from the authorize URL (not just the required ones)
  // so the ForgeRock goto chain passes through every OIDC parameter
  // (e.g. response_mode) without data loss.
  const params = {};
  for (const [key, value] of searchParams.entries()) {
    params[key] = value;
  }

  return params;
};

/**
 * In local development, force ForgeRock URLs to current origin so navigation
 * goes through local proxy (/openam/...) and can forward local cookies server-side.
 */
export const normalizeLocalForgeRockRedirect = (url) => {
  if (!url) {
    return url;
  }

  try {
    const parsedUrl = new URL(url, globalThis.location.origin);
    const isLocalEnvironment = isLocalAuthHost();
    const isForgeRockPath = parsedUrl.pathname.startsWith("/openam/");

    if (isLocalEnvironment && isForgeRockPath) {
      const localUrl = new URL(
        `${globalThis.location.origin}${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`,
      );

      return localUrl.toString();
    }

    return parsedUrl.toString();
  } catch (error) {
    return url;
  }
};
